//Solar Report
//Site Details
//Date Slicer - vwReportingDateTime - Date
//Array Details
//Date Slicer - vwReportingDateTime - Date
//End Use Slicer - vwEndUseNames - EndUseName
//Inverters
//Date Slicer - vwReportingDateTime - Date
//End Use Slicer - vwEndUseNames - EndUseName
//Inverter Slicer - vwEndUseNames - EndUseName

import constants from '../constants';

const PAGE_SITE_DETAILS = 'Site Details';
const PAGE_ARRAY_DETAILS = 'Array Details';
const PAGE_INVERTERS = 'Inverters';

// Remembers the last End Use Slicer selection on the Inverters page so the
// Inverter Slicer can be re-derived from it (initial load or navigation).
let selectedInverterTopEndUseName = null;

/**
 * Parses the Inverters JSON string from the report parameters.
 * @param {Object} reportParameters - The report parameters.
 * @returns {Array} - The list of { TopEndUseName, InverterEndUseName } pairs.
 */
const getInverters = (reportParameters) => {
  const { Inverters } = reportParameters;

  if (!Inverters) return [];

  return typeof Inverters === 'string' ? JSON.parse(Inverters) : Inverters;
};

/**
 * Finds the inverter data matching a TopEndUseName, falling back to the first entry.
 * @param {Object} reportParameters - The report parameters.
 * @param {String} topEndUseName - The Top End Use name to match.
 * @returns {Object|null} - The matching { TopEndUseName, InverterEndUseName } pair.
 */
const getInverterDataByTopEndUseName = (reportParameters, topEndUseName) => {
  const inverters = getInverters(reportParameters);

  if (!inverters.length) return null;
  if (!topEndUseName) return inverters[0];

  const trimmed = topEndUseName.trim().toLowerCase();

  return (
    inverters.find(
      (data) => data.TopEndUseName.trim().toLowerCase() === trimmed
    ) || inverters[0]
  );
};

/**
 * Reads a slicer visual's own currently-bound filter target, falling back to
 * the given default when the visual has no filter applied yet. Reusing the
 * visual's own reported target avoids "Filters target doesn't match slicer
 * target" errors that happen when an assumed table/column doesn't exactly
 * match what the visual is actually bound to.
 * @param {Object} visual - The slicer visual.
 * @param {Object} fallbackTarget - The target to use if none can be read.
 * @returns {Promise<Object>}
 */
const getVisualTarget = async (visual, fallbackTarget) => {
  const state = await visual.getSlicerState();
  return state?.filters?.[0]?.target || fallbackTarget;
};

/**
 * Sets the Date Slicer visual based on the StartDate/EndDate parameters.
 * @param {Object} reportParameters - The report parameters.
 * @param {Array} visuals - The visuals array.
 * @returns {Promise<void>}
 */
const setDataSlicerVisual = async (reportParameters, visuals) => {
  const dateSlicerVisual = visuals.find(
    (visual) => visual.type === 'slicer' && visual.title === 'Date Slicer'
  );

  if (!dateSlicerVisual) return;

  const target = await getVisualTarget(dateSlicerVisual, {
    table: 'vwReportingDateTime',
    column: 'Date',
  });

  await dateSlicerVisual.setSlicerState({
    filters: [
      {
        $schema: constants.ADVANCED_FILTER,
        target,
        logicalOperator: 'And',
        conditions: [
          {
            operator: 'GreaterThanOrEqual',
            value: reportParameters.StartDate,
          },
          {
            operator: 'LessThanOrEqual',
            value: reportParameters.EndDate,
          },
        ],
      },
    ],
  });
};

/**
 * Sets the End Use Slicer visual to a single value.
 * @param {Array} visuals - The visuals array.
 * @param {String} endUseName - The End Use name to select.
 * @returns {Promise<void>}
 */
const setEndUseSlicerVisual = async (visuals, endUseName) => {
  const endUseSlicerVisual = visuals.find(
    (visual) => visual.type === 'slicer' && visual.title === 'End Use Slicer'
  );

  if (!endUseSlicerVisual || !endUseName) return;

  const target = await getVisualTarget(endUseSlicerVisual, {
    table: 'vwEndUseNames',
    column: 'EndUseName',
  });

  await endUseSlicerVisual.setSlicerState({
    filters: [
      {
        $schema: constants.BASIC_FILTER,
        target,
        operator: 'In',
        values: [endUseName],
      },
    ],
  });
};

/**
 * Sets the Inverter Slicer visual to a single value.
 * @param {Array} visuals - The visuals array.
 * @param {String} inverterEndUseName - The Inverter End Use name to select.
 * @returns {Promise<void>}
 */
const setInverterSlicerVisual = async (visuals, inverterEndUseName) => {
  const inverterSlicerVisual = visuals.find(
    (visual) => visual.type === 'slicer' && visual.title === 'Inverter Slicer'
  );

  if (!inverterSlicerVisual || !inverterEndUseName) return;

  const target = await getVisualTarget(inverterSlicerVisual, {
    table: 'vwEndUseNames',
    column: 'EndUseName',
  });

  await inverterSlicerVisual.setSlicerState({
    filters: [
      {
        $schema: constants.BASIC_FILTER,
        target,
        operator: 'In',
        values: [inverterEndUseName],
      },
    ],
  });
};

const setSiteDetailsSlicers = async (reportParameters, visuals) => {
  await setDataSlicerVisual(reportParameters, visuals);
};

const setArrayDetailsSlicers = async (reportParameters, visuals) => {
  await setDataSlicerVisual(reportParameters, visuals);
  // The End Use Slicer on this page is intentionally left untouched -
  // the user controls it directly and Power BI keeps its own selection.
};

const setInvertersSlicers = async (reportParameters, visuals) => {
  await setDataSlicerVisual(reportParameters, visuals);

  if (!selectedInverterTopEndUseName) {
    const inverters = getInverters(reportParameters);
    selectedInverterTopEndUseName = inverters.length
      ? inverters[0].TopEndUseName
      : null;
  }

  const inverterData = getInverterDataByTopEndUseName(
    reportParameters,
    selectedInverterTopEndUseName
  );

  await setEndUseSlicerVisual(visuals, inverterData?.TopEndUseName);
  await setInverterSlicerVisual(visuals, inverterData?.InverterEndUseName);
};

/**
 * Applies the correct slicers for the given active page.
 * @param {String} pageDisplayName - The active page's display name.
 * @param {Object} reportParameters - The report parameters.
 * @param {Array} visuals - The visuals array for the active page.
 * @returns {Promise<void>}
 */
const setSlicersForPage = async (
  pageDisplayName,
  reportParameters,
  visuals
) => {
  switch (pageDisplayName) {
    case PAGE_ARRAY_DETAILS:
      await setArrayDetailsSlicers(reportParameters, visuals);
      break;
    case PAGE_INVERTERS:
      await setInvertersSlicers(reportParameters, visuals);
      break;
    case PAGE_SITE_DETAILS:
    default:
      await setSiteDetailsSlicers(reportParameters, visuals);
      break;
  }
};

/**
 * Records a user-driven End Use Slicer change on the Inverters page so the
 * Inverter Slicer can be re-derived from it. Self-contained to this page -
 * it does not read or write anything related to Array Details.
 * @param {String} topEndUseName - The newly selected Top End Use name.
 */
const onInverterTopEndUseChanged = (topEndUseName) => {
  selectedInverterTopEndUseName = topEndUseName;
};

export default {
  setSlicersForPage,
  setDataSlicerVisual,
  setEndUseSlicerVisual,
  setInverterSlicerVisual,
  getInverterDataByTopEndUseName,
  onInverterTopEndUseChanged,
};
